import { AuthenticatedHttp } from '@/http/axios';
import { getPlatformConfigs, getPlatforms } from '@/services/apps';
import { AppError } from '@/utils/api';
import {
  getWebhookAppPlatforms,
  normalizeWebhookPlatformsResponse,
  resolveWebhookDispatchedAt,
  resolveWebhookPlatform,
  resolveWebhookPlatformType,
  sortWebhooksByUpdatedAtDesc,
  WEBHOOK_PLATFORM_TYPES,
} from '@/utils/webhooks';

const WEBHOOKS_LIMIT = 1000;
export const WEBHOOK_DATA_SOURCES = Object.freeze({
  LEGACY: 'legacy',
  AGGREGATED: 'aggregated',
});
export const WEBHOOK_AGGREGATED_ENDPOINTS = Object.freeze({
  apps: '/app/platforms/apps/',
  recentActivities: '/app/events/all-history/',
});

const EMPTY_WEBHOOK_APPS_RESPONSE = Object.freeze({
  count: 0,
  results: [],
});
const EMPTY_WEBHOOK_ACTIVITIES_RESPONSE = Object.freeze({
  count: 0,
  next: null,
  previous: null,
  results: [],
});

export function getWebhookAppsSource() {
  return WEBHOOK_AGGREGATED_ENDPOINTS.apps
    ? WEBHOOK_DATA_SOURCES.AGGREGATED
    : WEBHOOK_DATA_SOURCES.LEGACY;
}

export function getWebhookActivitiesSource() {
  return WEBHOOK_AGGREGATED_ENDPOINTS.recentActivities
    ? WEBHOOK_DATA_SOURCES.AGGREGATED
    : WEBHOOK_DATA_SOURCES.LEGACY;
}

function sortWebhookActivities(items = []) {
  return [...items].sort(
    (left, right) =>
      new Date(resolveWebhookDispatchedAt(right) || 0).getTime() -
      new Date(resolveWebhookDispatchedAt(left) || 0).getTime()
  );
}

async function getPaginatedEndpointData(
  endpoint,
  { params = {}, limit = WEBHOOKS_LIMIT, maxPages = Number.POSITIVE_INFINITY } = {}
) {
  const fetchPage = async (page = 1, aggregatedResults = [], totalCount = 0) => {
    const response = await AuthenticatedHttp.get(endpoint, {
      params: {
        ...params,
        page,
        limit,
      },
    });

    const data = response?.data || {};
    const nextResults = aggregatedResults.concat(data?.results || []);
    const nextCount = totalCount || Number(data?.count) || 0;

    if (!data?.next || page >= maxPages) {
      return {
        count: nextCount || nextResults.length,
        results: nextResults,
      };
    }

    return fetchPage(page + 1, nextResults, nextCount);
  };

  return fetchPage();
}

export async function getWebhookStatusMetrics({ appId } = {}) {
  try {
    let response;

    if (appId !== undefined && appId !== null) {
      response = await AuthenticatedHttp.get('/app/events/status-metrics/', {
        params: { app_id: appId },
      });
    } else {
      response = await AuthenticatedHttp.get('/app/events/status-metrics/');
    }

    return response.data;
  } catch (error) {
    throw new AppError(error);
  }
}

export async function getWebhookPlatforms() {
  try {
    const data = await getPlatforms();
    const platforms = normalizeWebhookPlatformsResponse(data);

    return platforms.filter((platform) => WEBHOOK_PLATFORM_TYPES.includes(platform.type));
  } catch (error) {
    throw new AppError(error);
  }
}

async function getWebhookAppsFromPlatformConfigs({ platforms = [], search = '', filters = {} } = {}) {
  const webhookAppPlatforms = getWebhookAppPlatforms(platforms);

  if (!webhookAppPlatforms.length) {
    return EMPTY_WEBHOOK_APPS_RESPONSE;
  }

  const responses = await Promise.all(
    webhookAppPlatforms.map((platform) =>
      getPlatformConfigs({
        platformId: platform.id,
        page: 1,
        limit: WEBHOOKS_LIMIT,
        search,
        filters,
      }).then((response) => ({
        ...response,
        platformType: platform.type,
      }))
    )
  );

  const results = responses.flatMap((response) =>
    (response?.results || []).map((webhook) => ({
      ...webhook,
      platformType: response.platformType,
    }))
  );

  const uniqueResults = Array.from(new Map(results.map((webhook) => [webhook.id, webhook])).values());

  return {
    count: uniqueResults.length,
    results: sortWebhooksByUpdatedAtDesc(uniqueResults),
  };
}

function getWebhookAppsAggregatedParams({ platforms = [], filters = {}, search = '' } = {}) {
  const webhookAppPlatforms = getWebhookAppPlatforms(platforms);
  const platformIds = webhookAppPlatforms.map((platform) => platform.id).filter(Boolean);

  return {
    ...(platformIds.length > 0 && { platform: platformIds.join(',') }),
    ...(filters?.status?.length > 0 && { status: filters.status.join(',') }),
    ...(filters?.products?.length > 0 && { products: filters.products.join(',') }),
    ...(filters?.events?.length > 0 && { events: filters.events.join(',') }),
    ...(search && { name: search }),
  };
}

async function getWebhookAppsFromAggregatedEndpoint({ platforms = [], search = '', filters = {} } = {}) {
  const webhookAppPlatforms = getWebhookAppPlatforms(platforms);

  if (!webhookAppPlatforms.length) {
    return EMPTY_WEBHOOK_APPS_RESPONSE;
  }

  const response = await getPaginatedEndpointData(WEBHOOK_AGGREGATED_ENDPOINTS.apps, {
    params: getWebhookAppsAggregatedParams({
      platforms: webhookAppPlatforms,
      search,
      filters,
    }),
  });

  const results = (response?.results || []).map((webhook) => {
    const resolvedPlatform = resolveWebhookPlatform(webhook, webhookAppPlatforms);
    const resolvedPlatformType =
      resolveWebhookPlatformType(webhook, webhookAppPlatforms) || resolvedPlatform?.type || null;

    return {
      ...webhook,
      ...(resolvedPlatform?.id && { platformId: resolvedPlatform.id }),
      ...(resolvedPlatformType && { platformType: resolvedPlatformType }),
    };
  });

  return {
    count: response?.count || results.length,
    results: sortWebhooksByUpdatedAtDesc(results),
  };
}

export async function getWebhookApps({ platforms = [], search = '', filters = {} } = {}) {
  try {
    if (getWebhookAppsSource() === WEBHOOK_DATA_SOURCES.AGGREGATED) {
      return getWebhookAppsFromAggregatedEndpoint({ platforms, search, filters });
    }

    return getWebhookAppsFromPlatformConfigs({ platforms, search, filters });
  } catch (error) {
    throw new AppError(error);
  }
}

export async function getWebhookAppEventHistory({ appId, page = 1 }) {
  try {
    const response = await AuthenticatedHttp.get(`/app/events/${appId}/history/`, {
      params: { page },
    });

    return response.data;
  } catch (error) {
    throw new AppError(error);
  }
}

export async function getAllWebhookAppEventHistory({
  appId,
  currentPage = 1,
  results = [],
  maxPages = Number.POSITIVE_INFINITY,
} = {}) {
  try {
    const response = await getWebhookAppEventHistory({ appId, page: currentPage });
    const nextResults = results.concat(response?.results || []);

    if (!response?.next || currentPage >= maxPages) {
      return {
        count: Number(response?.count) || nextResults.length,
        results: sortWebhookActivities(nextResults),
      };
    }

    return getAllWebhookAppEventHistory({
      appId,
      currentPage: currentPage + 1,
      results: nextResults,
      maxPages,
    });
  } catch (error) {
    throw new AppError(error);
  }
}

async function getWebhookAppHistory(
  app,
  { currentPage = 1, results = [], maxPagesPerApp = Number.POSITIVE_INFINITY } = {}
) {
  const response = await getWebhookAppEventHistory({ appId: app.id, page: currentPage });

  const mergedResults = results.concat(
    (response?.results || []).map((activity) => ({
      ...activity,
      app: activity.app || {
        id: app.id,
        name: app.name,
        status: app.status,
        url: app.url,
      },
    }))
  );

  if (!response?.next || currentPage >= maxPagesPerApp) {
    return mergedResults;
  }

  return getWebhookAppHistory(app, {
    currentPage: currentPage + 1,
    results: mergedResults,
    maxPagesPerApp,
  });
}

async function getWebhookActivitiesFromAppHistories({
  apps = [],
  maxPagesPerApp = Number.POSITIVE_INFINITY,
} = {}) {
  if (!apps.length) {
    return [];
  }

  const activitiesByApp = await Promise.all(
    apps.map((app) => getWebhookAppHistory(app, { maxPagesPerApp }))
  );

  return sortWebhookActivities(activitiesByApp.flat());
}

async function getWebhookActivitiesFromAggregatedEndpoint({
  maxPages = Number.POSITIVE_INFINITY,
  limit = WEBHOOKS_LIMIT,
  lastDay = false,
} = {}) {
  const response = await getPaginatedEndpointData(WEBHOOK_AGGREGATED_ENDPOINTS.recentActivities, {
    params: {
      ...(lastDay && { last_day: true }),
    },
    limit,
    maxPages,
  });

  return sortWebhookActivities(response?.results || []);
}

async function getPaginatedWebhookActivitiesFromAggregatedEndpoint({
  page = 1,
  limit = WEBHOOKS_LIMIT,
  lastDay = false,
} = {}) {
  const response = await AuthenticatedHttp.get(WEBHOOK_AGGREGATED_ENDPOINTS.recentActivities, {
    params: {
      page,
      ...(limit && { limit }),
      ...(lastDay && { last_day: true }),
    },
  });
  const data = response?.data || EMPTY_WEBHOOK_ACTIVITIES_RESPONSE;

  return {
    count: Number(data?.count) || 0,
    next: data?.next || null,
    previous: data?.previous || null,
    results: sortWebhookActivities(data?.results || []),
  };
}

function paginateWebhookActivities(results = [], { page = 1, limit = WEBHOOKS_LIMIT } = {}) {
  const currentPage = Math.max(Number(page) || 1, 1);
  const rowsPerPage = Math.max(Number(limit) || WEBHOOKS_LIMIT, 1);
  const totalCount = results.length;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  return {
    count: totalCount,
    next: endIndex < totalCount ? String(currentPage + 1) : null,
    previous: currentPage > 1 ? String(currentPage - 1) : null,
    results: results.slice(startIndex, endIndex),
  };
}

export async function getWebhookActivities({
  apps = [],
  maxPagesPerApp = Number.POSITIVE_INFINITY,
  lastDay = false,
  limit = WEBHOOKS_LIMIT,
  page = 1,
  paginated = false,
} = {}) {
  try {
    if (getWebhookActivitiesSource() === WEBHOOK_DATA_SOURCES.AGGREGATED) {
      if (paginated) {
        return getPaginatedWebhookActivitiesFromAggregatedEndpoint({
          page,
          limit,
          lastDay,
        });
      }

      return getWebhookActivitiesFromAggregatedEndpoint({
        apps,
        maxPages: maxPagesPerApp,
        lastDay,
        limit,
      });
    }

    if (paginated) {
      const activities = await getWebhookActivitiesFromAppHistories({ apps, maxPagesPerApp });
      return paginateWebhookActivities(activities, { page, limit });
    }

    return getWebhookActivitiesFromAppHistories({ apps, maxPagesPerApp });
  } catch (error) {
    throw new AppError(error);
  }
}
